import React from "react";
import {Container, Table} from "react-bootstrap";
import {usePage} from "@inertiajs/react";
import {Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis} from "recharts";
import NutzungLayout from "../../Components/Nutzung/NutzungLayout.jsx";
import NutzungWidget from "../../Components/Nutzung/NutzungWidget.jsx";
import useNutzungFilter from "../../Components/Nutzung/useNutzungFilter.js";
import useStatisticsData from "../../Components/Statistics/useStatisticsData.js";
import {bereichFarbe, bereichLabel, formatDuration} from "../../Components/Nutzung/format.js";
import {formatDateDE, formatNumber, formatPercent, GROUP_PALETTE} from "../../Components/Statistics/format.js";
import {MitarbeiterLink, SeiteLink} from "../../Components/Nutzung/Links.jsx";
import HelpTooltip from "../../Components/HelpTooltip.jsx";

/** Pflicht-Kurzhinweis an der Kennzahl "aktive Zeit" (Spezifikation
 * Abschnitt 5) - EINE Textquelle, damit sie überall im gleichen Wortlaut
 * erscheint. */
const AKTIVE_ZEIT_HINWEIS =
    "Misst, wie dicht im Ordermanagement gearbeitet wird. Telefonate, Arbeit in Weclapp oder " +
    "im Lager sind darin nicht enthalten - die Zahl ist kein Maß für Arbeitsleistung.";

/** Kennzahlenzeile - "aktive Zeit" bekommt PFLICHTGEMÄSS den erklärenden
 * Kurzhinweis aus der Spezifikation (Abschnitt 5), hier über das ohnehin
 * verwendete help-Feld von NutzungWidget/ChartCard ausgeliefert. */
function Kennzahlen({kennzahlen, loading, error}) {
    return (
        <NutzungWidget
            title="Kennzahlen"
            loading={loading}
            error={error}
            empty={!loading && !error && !kennzahlen}
            height={90}
        >
            <div className="row g-3">
                <div className="col-sm-4">
                    <div className="small text-muted">Mitarbeiter</div>
                    <div className="fs-4 fw-semibold">{formatNumber(kennzahlen?.nutzer)}</div>
                </div>
                <div className="col-sm-4">
                    <div className="small text-muted">Ereignisse</div>
                    <div className="fs-4 fw-semibold">{formatNumber(kennzahlen?.ereignisse)}</div>
                </div>
                <div className="col-sm-4">
                    <div className="small text-muted d-inline-flex align-items-center gap-1">
                        Aktive Zeit
                        <HelpTooltip placement="top" size={0.75}>{AKTIVE_ZEIT_HINWEIS}</HelpTooltip>
                    </div>
                    <div className="fs-4 fw-semibold">{formatDuration(kennzahlen?.aktiv_sekunden)}</div>
                </div>
            </div>
        </NutzungWidget>
    );
}

/** Wer arbeitet in diesem Bereich - Namen verlinkt (Kernanforderung 2),
 * Anteil als kleiner Balken statt nackter Prozentzahl. */
function MitarbeiterListe({rows, loading, error}) {
    const zeilen = Array.isArray(rows) ? [...rows].sort((a, b) => (b.ereignisse || 0) - (a.ereignisse || 0)) : [];
    return (
        <NutzungWidget
            title="Wer hier arbeitet"
            help="Mitarbeiter mit mindestens einem Ereignis in diesem Bereich im gewählten Zeitraum."
            loading={loading}
            error={error}
            empty={!loading && !error && zeilen.length === 0}
            height={280}
        >
            <div style={{overflowX: "auto"}}>
                <Table hover size="sm" className="align-middle mb-0">
                    <thead>
                        <tr>
                            <th>Mitarbeiter</th>
                            <th className="text-end">Ereignisse</th>
                            <th className="text-end">Aktive Zeit</th>
                            <th style={{width: 160}}>Anteil</th>
                        </tr>
                    </thead>
                    <tbody>
                        {zeilen.map((zeile) => (
                            <tr key={zeile.user_id}>
                                <td className="fw-semibold">
                                    <MitarbeiterLink id={zeile.user_id} name={zeile.name} />
                                </td>
                                <td className="text-end">{formatNumber(zeile.ereignisse)}</td>
                                <td className="text-end">{formatDuration(zeile.aktiv_sekunden)}</td>
                                <td>
                                    <div className="d-flex align-items-center gap-2">
                                        <div className="flex-grow-1 rounded-pill bg-body-secondary" style={{height: 6}}>
                                            <div
                                                className="rounded-pill"
                                                style={{
                                                    height: 6,
                                                    width: `${Math.min(100, Math.round((zeile.anteil || 0) * 100))}%`,
                                                    background: GROUP_PALETTE[0],
                                                }}
                                            />
                                        </div>
                                        <span className="small text-muted" style={{minWidth: 40, textAlign: "right"}}>
                                            {formatPercent((zeile.anteil || 0) * 100)}
                                        </span>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>
        </NutzungWidget>
    );
}

/** Welche Seiten in diesem Bereich benutzt werden - Seitenname verlinkt. */
function SeitenListe({rows, loading, error}) {
    const zeilen = Array.isArray(rows) ? [...rows].sort((a, b) => (b.aufrufe || 0) - (a.aufrufe || 0)) : [];
    return (
        <NutzungWidget
            title="Welche Seiten hier benutzt werden"
            loading={loading}
            error={error}
            empty={!loading && !error && zeilen.length === 0}
            height={280}
        >
            <div style={{overflowX: "auto"}}>
                <Table hover size="sm" className="align-middle mb-0">
                    <thead>
                        <tr>
                            <th>Seite</th>
                            <th className="text-end">Aufrufe</th>
                            <th className="text-end">Verschiedene Nutzer</th>
                        </tr>
                    </thead>
                    <tbody>
                        {zeilen.map((zeile) => (
                            <tr key={zeile.route}>
                                <td className="fw-semibold">
                                    <SeiteLink route={zeile.route} />
                                </td>
                                <td className="text-end">{formatNumber(zeile.aufrufe)}</td>
                                <td className="text-end">{formatNumber(zeile.nutzer)}</td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>
        </NutzungWidget>
    );
}

/** Verlauf der Ereignisse dieses Bereichs über den Zeitraum. */
function Verlauf({rows, farbe, loading, error}) {
    const zeilen = Array.isArray(rows) ? rows : [];
    return (
        <NutzungWidget
            title="Verlauf"
            help="Ereignisse je Tag im gewählten Zeitraum."
            loading={loading}
            error={error}
            empty={!loading && !error && zeilen.length === 0}
            height={240}
        >
            <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={zeilen} margin={{top: 8, right: 12, left: 0, bottom: 0}}>
                    <defs>
                        <linearGradient id="nutzung-bereich-fill" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={farbe} stopOpacity={0.35} />
                            <stop offset="95%" stopColor={farbe} stopOpacity={0.03} />
                        </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--bs-border-color, #dee2e6)" />
                    <XAxis dataKey="tag" tickFormatter={formatDateDE} tick={{fontSize: 12}} axisLine={false} tickLine={false} />
                    <YAxis tickFormatter={formatNumber} tick={{fontSize: 12}} axisLine={false} tickLine={false} width={54} />
                    <Tooltip formatter={(value) => [formatNumber(value), "Ereignisse"]} labelFormatter={(label) => formatDateDE(label)} />
                    <Area type="monotone" dataKey="ereignisse" stroke={farbe} strokeWidth={2} fill="url(#nutzung-bereich-fill)" />
                </AreaChart>
            </ResponsiveContainer>
        </NutzungWidget>
    );
}

/**
 * Detailseite eines einzelnen Bereichs (Auftrag Teil 2, Endpunkt
 * /nutzung/daten/bereich/{bereich} - Feldnamen aus Spezifikation Abschnitt 3
 * bindend: kennzahlen, mitarbeiter, seiten, verlauf in EINER Antwort).
 */
export default function NutzungBereich() {
    const {bereich} = usePage().props;
    const nutzungFilters = useNutzungFilter();
    const {filters} = nutzungFilters;
    const {data, loading, error} = useStatisticsData(`/nutzung/daten/bereich/${encodeURIComponent(bereich)}`, filters);

    const farbe = bereichFarbe(bereich);

    return (
        <Container fluid className="pt-2 pb-4">
            <NutzungLayout
                gruppe="Bereiche"
                titel={bereichLabel(bereich)}
                breadcrumbLabel={bereichLabel(bereich)}
                nutzungFilters={nutzungFilters}
                showBereich={false}
            >
                <div className="d-flex flex-column gap-3">
                    <div className="d-flex align-items-center gap-2">
                        <span
                            aria-hidden="true"
                            style={{width: 14, height: 14, borderRadius: 4, background: farbe, display: "inline-block"}}
                        />
                        <span className="text-muted small">Farbe dieses Bereichs in der gesamten Auswertung.</span>
                    </div>

                    <Kennzahlen kennzahlen={data?.kennzahlen} loading={loading} error={error} />

                    <div className="row g-3">
                        <div className="col-lg-6">
                            <MitarbeiterListe rows={data?.mitarbeiter} loading={loading} error={error} />
                        </div>
                        <div className="col-lg-6">
                            <SeitenListe rows={data?.seiten} loading={loading} error={error} />
                        </div>
                    </div>

                    <Verlauf rows={data?.verlauf} farbe={farbe} loading={loading} error={error} />
                </div>
            </NutzungLayout>
        </Container>
    );
}
